/**
 * Pure-function versions of all helpers in usePermissionCheck.ts.
 * These take SecurityRolePermission[] directly instead of reading from a hook,
 * making them fully testable without React context.
 *
 * The logic here mirrors src/core/hooks/usePermissionCheck.ts exactly.
 */

export interface PermissionEntry {
  '@type': 'Permission';
  '@id': string;
  id: number;
  label: string;
  permissionGroup: string;
}

export interface SecurityRolePermission {
  '@type': 'SecurityRolePermission';
  '@id': string;
  id: number;
  permission: PermissionEntry;
  view: boolean;
  edit: boolean;
  delete: boolean;
  execute: boolean;
}

export type PermissionAction = 'view' | 'edit' | 'delete' | 'execute';

// ─── Core helpers ─────────────────────────────────────────────────────────────

export function checkPermission(
  perms: SecurityRolePermission[],
  label: string,
  action: PermissionAction = 'view',
): boolean {
  if (!perms?.length) return false;
  const found = perms.find((p) => p.permission.label === label);
  if (!found) return false;
  return !!found[action];
}

export function hasAnyPermissionInGroup(
  perms: SecurityRolePermission[],
  groupId: string,
  action: PermissionAction = 'view',
): boolean {
  if (!perms?.length) return false;
  return perms.some(
    (p) =>
      p.permission.permissionGroup === `/api/permission_groups/${groupId}` &&
      !!p[action],
  );
}

// ─── Settings (Group 2) helpers ───────────────────────────────────────────────

export function hasViewPermission(
  perms: SecurityRolePermission[],
  label: string,
): boolean {
  if (label === 'General') return hasAnyPermissionInGroup(perms, '2');
  return checkPermission(perms, label, 'view');
}

export function hasEditPermission(
  perms: SecurityRolePermission[],
  label: string,
): boolean {
  if (label === 'General') return hasAnyPermissionInGroup(perms, '2', 'edit');
  return checkPermission(perms, label, 'edit');
}

export function hasDeletePermission(
  perms: SecurityRolePermission[],
  label: string,
): boolean {
  if (label === 'General') return hasAnyPermissionInGroup(perms, '2', 'delete');
  return checkPermission(perms, label, 'delete');
}

export function hasExecutePermission(
  perms: SecurityRolePermission[],
  label: string,
): boolean {
  if (label === 'General') return hasAnyPermissionInGroup(perms, '2', 'execute');
  return checkPermission(perms, label, 'execute');
}

// ─── Module (Group 4) helpers ─────────────────────────────────────────────────

function findModulePerm(
  perms: SecurityRolePermission[],
  label: string,
): SecurityRolePermission | undefined {
  return perms.find(
    (p) =>
      p.permission.permissionGroup === '/api/permission_groups/4' &&
      p.permission.label === label,
  );
}

export function hasModuleViewPermission(
  perms: SecurityRolePermission[],
  label: string,
): boolean {
  if (!perms?.length) return false;
  const found = findModulePerm(perms, label);
  return !!found && !!found.view;
}

export function hasModuleEditPermission(
  perms: SecurityRolePermission[],
  label: string,
): boolean {
  if (!perms?.length) return false;
  const found = findModulePerm(perms, label);
  return !!found && !!found.edit;
}

export function hasModuleDeletePermission(
  perms: SecurityRolePermission[],
  label: string,
): boolean {
  if (!perms?.length) return false;
  const found = findModulePerm(perms, label);
  return !!found && !!found.delete;
}

export function hasModuleExecutePermission(
  perms: SecurityRolePermission[],
  label: string,
): boolean {
  if (!perms?.length) return false;
  const found = findModulePerm(perms, label);
  return !!found && !!found.execute;
}
